// Pair Plotのレンダラー実装

import React, { useRef, useState, useEffect } from 'react';
import { GraphRenderer, PairPlotConfig, GraphCallbacks } from '../../../../types/graph-builder.types';
import { DataSet } from '../../../../types';
import { getColumnById, getColumnData } from '../../../utils/column-utils';
import { ScatterPlot } from '../../ScatterPlot';
import { Histogram } from '../../Histogram';

// レイアウト定数
const LABEL_WIDTH = 80;     // 左側ラベルの幅（px）
const LABEL_HEIGHT = 24;    // 上部ラベルの高さ（px）
const MIN_CELL_SIZE = 80;   // セルの最小サイズ（px）
const MAX_CELL_SIZE = 200;  // セルの最大サイズ（px）
const INFO_HEIGHT = 32;     // 下部の情報表示エリアの高さ（px）
const TITLE_HEIGHT = 28;    // タイトルの高さ（px）

interface PairPlotContentProps {
  config: PairPlotConfig;
  dataset: DataSet;
  callbacks: GraphCallbacks;
}

/**
 * PairPlotのコンテンツコンポーネント
 * ResizeObserverでコンテナサイズを検出し、セルサイズを動的に計算
 */
function PairPlotContent({ config, dataset, callbacks }: PairPlotContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null);

  // ResizeObserverでコンテナサイズを監視
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const columns = config.columns || [];
  const n = columns.length;

  if (n < 2) {
    return <div>Please select at least 2 variables</div>;
  }

  // セルサイズを計算
  let cellSize = MAX_CELL_SIZE;
  if (containerSize) {
    const titleOffset = config.title ? TITLE_HEIGHT : 0;
    const availableWidth = containerSize.width - LABEL_WIDTH;
    const availableHeight = containerSize.height - LABEL_HEIGHT - INFO_HEIGHT - titleOffset;

    // 利用可能なスペースに基づいてセルサイズを計算
    const maxCellByWidth = Math.floor(availableWidth / n);
    const maxCellByHeight = Math.floor(availableHeight / n);
    cellSize = Math.min(maxCellByWidth, maxCellByHeight, MAX_CELL_SIZE);
    cellSize = Math.max(cellSize, MIN_CELL_SIZE);
  }

  const gap = 0;

  // onPointClickハンドラーを作成（ScatterPlot用）
  const handlePointClick = callbacks.onElementSelect
    ? (dataIndices: number[], isAdditive?: boolean) => {
        callbacks.onElementSelect!(dataIndices, isAdditive || false);
      }
    : undefined;

  // onBinClickハンドラーを作成（Histogram用）
  const handleBinClick = callbacks.onElementSelect
    ? (_minValue: number, _maxValue: number, dataIndices: number[], isAdditive?: boolean) => {
        callbacks.onElementSelect!(dataIndices, isAdditive || false);
      }
    : undefined;

  // グリッドレイアウトのスタイル
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${n}, ${cellSize}px)`,
    gridTemplateRows: `repeat(${n}, ${cellSize}px)`,
    gap: `${gap}px`,
    backgroundColor: '#f8f9fa',
    borderRadius: '4px'
  };

  // ラベル用のスタイル
  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#495057',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e9ecef',
    borderRadius: '3px',
    padding: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {config.title && <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', flexShrink: 0 }}>{config.title}</h3>}

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* 変数名ラベル（上部） */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${n}, ${cellSize}px)`,
          gap: `${gap}px`,
          marginBottom: `${gap}px`,
          marginLeft: `${LABEL_WIDTH + gap}px`,
          height: `${LABEL_HEIGHT}px`,
        }}>
          {columns.map(colId => {
            const col = getColumnById(dataset, colId);
            return (
              <div key={colId} style={{ ...labelStyle, height: `${LABEL_HEIGHT}px` }}>
                {col?.name || colId}
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex' }}>
          {/* 変数名ラベル（左側） */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: `${gap}px`, marginRight: `${gap}px` }}>
            {columns.map(colId => {
              const col = getColumnById(dataset, colId);
              return (
                <div key={colId} style={{ ...labelStyle, height: `${cellSize}px`, width: `${LABEL_WIDTH}px`, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                  {col?.name || colId}
                </div>
              );
            })}
          </div>

          {/* グラフグリッド */}
          <div style={gridStyle}>
            {columns.map((yColId, rowIndex) => (
              columns.map((xColId, colIndex) => {
                const key = `${rowIndex}-${colIndex}`;

                // 対角線上：Histogram
                if (rowIndex === colIndex) {
                  const column = getColumnById(dataset, xColId);
                  const columnData = getColumnData(dataset, xColId);

                  if (!column || !columnData) {
                    return <div key={key}>Error</div>;
                  }

                  // 数値データのみを抽出
                  const numericData: number[] = [];
                  const originalData: { value: number; index: number }[] = [];

                  columnData.forEach((value, index) => {
                    if (typeof value === 'number' && !isNaN(value)) {
                      numericData.push(value);
                      originalData.push({ value, index });
                    }
                  });

                  return (
                    <div key={key} style={{ width: `${cellSize}px`, height: `${cellSize}px`, backgroundColor: 'white', borderRadius: '3px', overflow: 'hidden' }}>
                      <Histogram
                        data={numericData}
                        bins={10}
                        width={cellSize}
                        height={cellSize}
                        originalData={originalData}
                        onBinClick={handleBinClick}
                        selectedIndices={callbacks.selectedRows}
                        hideControls={true}
                        compact={true}
                      />
                    </div>
                  );
                }

                // 非対角線：Scatter Plot
                return (
                  <div key={key} style={{ width: `${cellSize}px`, height: `${cellSize}px`, backgroundColor: 'white', borderRadius: '3px', overflow: 'hidden' }}>
                    <ScatterPlot
                      dataset={dataset}
                      xColumn={xColId}
                      yColumn={yColId}
                      width={cellSize}
                      height={cellSize}
                      selectedRows={callbacks.selectedRows}
                      onPointClick={handlePointClick}
                      hideControls={true}
                      compact={true}
                    />
                  </div>
                );
              })
            ))}
          </div>
        </div>
      </div>

      {/* 情報表示 */}
      <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d', flexShrink: 0 }}>
        {n} × {n} matrix ({n * n} plots) | Diagonal: Histograms | Off-diagonal: Scatter plots
      </div>
    </div>
  );
}

export const pairPlotRenderer: GraphRenderer<PairPlotConfig> = {
  validate(config: PairPlotConfig, dataset: DataSet): string | null {
    // データセットの検証
    if (!dataset || !dataset.schema || !dataset.schema.columns) {
      return 'Invalid dataset';
    }

    // 列の選択数チェック
    if (!config.columns || config.columns.length === 0) {
      return 'Please select at least 2 variables';
    }

    if (config.columns.length < 2) {
      return 'Please select at least 2 variables for pair plot';
    }

    // 各列の存在確認と型チェック
    for (const columnId of config.columns) {
      const column = getColumnById(dataset, columnId);
      if (!column) {
        return `Column "${columnId}" not found in dataset`;
      }
      if (column.type !== 'int64' && column.type !== 'float64') {
        return `Column "${column.name}" must be numeric (found: ${column.type})`;
      }
    }

    return null;
  },

  render(config: PairPlotConfig, dataset: DataSet, callbacks: GraphCallbacks) {
    return (
      <PairPlotContent
        config={config}
        dataset={dataset}
        callbacks={callbacks}
      />
    );
  }
};
